import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { http } from '@/lib/api/client'

/** One customer's feedback: their rating and words, and how (if at all) it's quoted on arvaseo.com. */
export interface Feedback {
  id: string
  rating: number
  message: string | null
  source: 'prompt' | 'menu'
  status: 'new' | 'read' | 'archived'
  note: string | null
  may_quote: boolean
  display_name: string | null
  display_title: string | null
  consented_at: string | null
  on_homepage: boolean
  homepage_quote: string | null
  homepage_position: number | null
  /** What the homepage shows (or would): the part picked, or the whole message. */
  shown_quote: string | null
  published_at: string | null
  published_by: string | null
  /** Why it can't go on the homepage, or null when it can. */
  blocker: string | null
  person: { id: string; name: string; email: string; closed: boolean; internal: boolean }
  workspace: { id: string; name: string } | null
  created_at: string | null
}

export interface FeedbackSummary {
  total: number
  average: number | null
  last_30_days: number
  average_30_days: number | null
  by_rating: Record<'1' | '2' | '3' | '4' | '5', number>
  new: number
  may_quote: number
  on_homepage: number
}

/**
 * A thumbs up or down on one of Arva Assistant's replies (2026-10-26), with the website, the start
 * of the reply and the person's comment. Never quoted on the homepage.
 */
export interface AssistantRating {
  id: string
  source: 'Arva Assistant'
  rating: 'up' | 'down'
  comment: string | null
  /** The reply's first 300 characters. */
  reply: string
  website: { id: string; domain: string; name: string | null }
  person: { id: string; name: string; email: string; closed: boolean; internal: boolean }
  workspace: { id: string; name: string } | null
  created_at: string | null
  updated_at: string | null
}

/** Customers' ratings of Arva's replies (the team's own accounts left out). */
export interface AssistantRatingSummary {
  total: number
  up: number
  down: number
  last_30_days: number
  down_30_days: number
}

export interface FeedbackList {
  data: Feedback[]
  /** Arva Assistant reply ratings, newest first. */
  assistant?: AssistantRating[]
  meta: {
    summary: FeedbackSummary
    assistant_summary?: AssistantRatingSummary
    can_publish: boolean
    max_quote: number
  }
}

const key = ['feedback'] as const

export function useFeedback() {
  return useQuery({ queryKey: key, queryFn: () => http.get<FeedbackList>('/feedback') })
}

/** Read or archived, and the team's note. */
export function useUpdateFeedback() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...input }: { id: string; status?: Feedback['status']; note?: string | null }) =>
      http.patch<FeedbackList>(`/feedback/${id}`, input),
    onSuccess: (data) => queryClient.setQueryData(key, data),
  })
}

/** On the homepage or off it (owners and admins), with the part to show and its place. */
export function useFeedbackHomepage() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      ...input
    }: {
      id: string
      on_homepage: boolean
      quote?: string | null
      position?: number | null
    }) => http.put<FeedbackList>(`/feedback/${id}/homepage`, input),
    onSuccess: (data) => queryClient.setQueryData(key, data),
  })
}
